// Car valuation block. Brand + model come from our taxonomy (model = term
// NAME, which nomermd matches); the rest hydrates from /valuation/options, and
// "Afișează" fetches /valuation/price → typical price + monthly chart. Every
// call goes through our proxy (inc/valuation.php); the upstream token is never
// exposed. See template-parts/sections/valuation.php.
import { qs, qsa, on } from '../lib/dom.js';
import { t } from '../lib/i18n.js';
import { enhanceSelect } from './searchable-select.js';

const API = (typeof window !== 'undefined' && window.AP_DATA && window.AP_DATA.apiBase)
  ? window.AP_DATA.apiBase
  : '/wp-json/autoplaza/v1/';

function nfmt(n) {
  return new Intl.NumberFormat('ro-RO').format(Math.round(Number(n) || 0));
}
function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
async function getJSON(url) {
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    const data = await res.json().catch(() => null);
    return { ok: res.ok, status: res.status, data };
  } catch (e) {
    return { ok: false, status: 0, data: null };
  }
}

// "2026-07" → "iul. 2026" (localized short month).
function monthLabel(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym));
  if (!m) return String(ym);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  try {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'ro-RO', { month: 'short', year: 'numeric' }).format(d);
  } catch (e) {
    return ym;
  }
}

// Fill gaps in the monthly history: walk a continuous month axis from the first
// to the last data point and carry the previous month's value forward for any
// missing month, so the chart never skips a month or breaks the line (client
// request). Filled months are flagged so callers can tell them apart if needed.
function fillHistoryGaps(history) {
  const pts = (history || [])
    .filter((h) => h && h.month != null && h.avg_price != null)
    .slice()
    .sort((a, b) => String(a.month).localeCompare(String(b.month)));
  const parse = (ym) => { const m = /^(\d{4})-(\d{2})$/.exec(String(ym)); return m ? { y: Number(m[1]), m: Number(m[2]) } : null; };
  const first = pts.length ? parse(pts[0].month) : null;
  const last = pts.length ? parse(pts[pts.length - 1].month) : null;
  if (!first || !last) return pts;
  const byMonth = new Map(pts.map((p) => [p.month, p]));
  const out = [];
  let y = first.y, mo = first.m, prev = null;
  for (let guard = 0; guard < 240; guard++) { // 20yr cap - can't loop forever
    const key = `${y}-${String(mo).padStart(2, '0')}`;
    if (byMonth.has(key)) {
      prev = byMonth.get(key);
      out.push(prev);
    } else if (prev) {
      out.push({ month: key, avg_price: prev.avg_price, ads_count: 0, filled: true });
    }
    if (y === last.y && mo === last.m) break;
    mo += 1; if (mo > 12) { mo = 1; y += 1; }
  }
  return out;
}

// Dependency-free SVG area chart of the monthly price history.
function renderChart(host, history) {
  const pts = fillHistoryGaps(history);
  if (pts.length < 2) {
    host.innerHTML = `<p class="ap-valuation__note">${esc(t('val_chart_none', 'Prea puține date pentru grafic.'))}</p>`;
    return;
  }
  // Responsive canvas: on phones the SVG viewBox is scaled down to the card
  // width, so a 760-wide viewBox shrinks the label text to ~5px. Use a smaller
  // viewBox on mobile so everything (incl. the SVG-unit label font) scales UP to
  // a readable visual size. All geometry below derives from W/H, so shrinking W
  // uniformly enlarges the whole chart on screen. Desktop is unchanged (760).
  const mobile = (typeof window !== 'undefined') && window.innerWidth <= 620;
  const W = mobile ? 380 : 760;
  const H = mobile ? 260 : 300;
  const padL = mobile ? 52 : 56, padR = mobile ? 12 : 16, padT = 16, padB = 40;
  const iw = W - padL - padR, ih = H - padT - padB;
  const vals = pts.map((p) => Number(p.avg_price));
  let min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || Math.max(1, max * 0.1);
  min = Math.max(0, min - span * 0.15);
  max = max + span * 0.15;
  const x = (i) => padL + (pts.length === 1 ? iw / 2 : (i / (pts.length - 1)) * iw);
  const y = (v) => padT + ih - ((v - min) / (max - min)) * ih;

  // y grid: 5 lines
  const ticks = 5;
  let grid = '';
  for (let g = 0; g <= ticks; g++) {
    const v = min + (g / ticks) * (max - min);
    const yy = y(v);
    grid += `<line x1="${padL}" y1="${yy.toFixed(1)}" x2="${W - padR}" y2="${yy.toFixed(1)}" class="ap-valuation__chart-grid"/>`;
    grid += `<text x="${padL - 8}" y="${(yy + 4).toFixed(1)}" text-anchor="end" class="ap-valuation__chart-ylabel">${nfmt(v)}</text>`;
  }
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(Number(p.avg_price)).toFixed(1)}`).join(' ');
  const area = `${line} L${x(pts.length - 1).toFixed(1)},${(padT + ih).toFixed(1)} L${x(0).toFixed(1)},${(padT + ih).toFixed(1)} Z`;
  const dots = pts.map((p, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(Number(p.avg_price)).toFixed(1)}" r="3.5" class="ap-valuation__chart-dot" data-i="${i}"/>`).join('');
  // Transparent, larger circles = easy hover/tap targets for the tooltip.
  const hits = pts.map((p, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(Number(p.avg_price)).toFixed(1)}" r="16" class="ap-valuation__chart-hit" data-i="${i}" tabindex="0" role="img" aria-label="${esc(monthLabel(p.month))}: ${nfmt(p.avg_price)} €"/>`).join('');
  // x labels: fewer on the narrower mobile canvas so month labels don't overlap
  const step = Math.max(1, Math.ceil(pts.length / (mobile ? 4 : 6)));
  let xlabels = '';
  pts.forEach((p, i) => {
    if (i % step === 0 || i === pts.length - 1) {
      xlabels += `<text x="${x(i).toFixed(1)}" y="${H - 12}" text-anchor="middle" class="ap-valuation__chart-xlabel">${esc(monthLabel(p.month))}</text>`;
    }
  });
  host.innerHTML = `<div class="ap-valuation__chart-inner">
    <svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" class="ap-valuation__chart-svg">
      ${grid}
      <path d="${area}" class="ap-valuation__chart-area"/>
      <path d="${line}" class="ap-valuation__chart-line"/>
      ${dots}
      ${hits}
      ${xlabels}
    </svg>
    <div class="ap-valuation__tooltip" data-role="tip" hidden></div>
  </div>`;

  // Hover/focus a point → floating tooltip with the exact month + price.
  const inner = host.querySelector('.ap-valuation__chart-inner');
  const tip = host.querySelector('[data-role="tip"]');
  const dotEls = host.querySelectorAll('.ap-valuation__chart-dot');
  function showTip(i) {
    const p = pts[i];
    if (!p) return;
    tip.innerHTML = `<span class="ap-valuation__tip-month">${esc(monthLabel(p.month))}</span><span class="ap-valuation__tip-price">${nfmt(p.avg_price)} €</span>`;
    const hit = host.querySelector(`.ap-valuation__chart-hit[data-i="${i}"]`);
    const hr = hit.getBoundingClientRect();
    const ir = inner.getBoundingClientRect();
    tip.style.left = (hr.left + hr.width / 2 - ir.left) + 'px';
    tip.style.top = (hr.top + hr.height / 2 - ir.top) + 'px';
    tip.hidden = false;
    dotEls.forEach((d) => d.classList.toggle('is-active', Number(d.dataset.i) === i));
  }
  function hideTip() {
    tip.hidden = true;
    dotEls.forEach((d) => d.classList.remove('is-active'));
  }
  host.querySelectorAll('.ap-valuation__chart-hit').forEach((h) => {
    const i = Number(h.dataset.i);
    on(h, 'mouseenter', () => showTip(i));
    on(h, 'focus', () => showTip(i));
    on(h, 'mouseleave', hideTip);
    on(h, 'blur', hideTip);
  });
}

export function initValuation(root = document) {
  const el = qs('[data-component="valuation"]', root);
  if (!el) return;
  const $ = (role) => qs(`[data-role="${role}"]`, el);

  const brand = $('brand'), model = $('model'), generation = $('generation');
  const startYear = $('start_year'), endYear = $('end_year');
  const fuel = $('fuel'), transmission = $('transmission'), drive = $('drive_type'), body = $('body_type');
  const mMin = $('mileage_min'), mMax = $('mileage_max');
  const mMinS = $('mileage_min_slider'), mMaxS = $('mileage_max_slider'), fill = $('slider-fill');
  const submit = $('submit'), status = $('status'), result = $('result'), priceEl = $('price'), chart = $('chart');

  const optionSelects = [generation, fuel, transmission, drive, body, startYear, endYear];
  const mileageInputs = [mMin, mMax, mMinS, mMaxS];
  let mileageFloor = 0, mileageCeil = 300000; // client cap: max mileage 300k km

  // Type-to-filter comboboxes over the native brand + model selects. These stay
  // the source of truth: valuation.js keeps setting brand.innerHTML/model.value
  // and toggling .disabled; the combobox re-reads options live and dispatches a
  // real 'change' on pick, so the on(brand,'change') / on(model,'change')
  // cascades below fire unchanged. See searchable-select.js.
  enhanceSelect(brand, {
    placeholder: brand.dataset.ph || t('val_brand_ph', 'Marca'),
    noResults: t('search_no_results', 'Niciun rezultat'),
  });
  enhanceSelect(model, {
    placeholder: model.dataset.ph || t('val_model_ph', 'Model'),
    noResults: t('search_no_results', 'Niciun rezultat'),
  });

  function setStatus(msg, kind) {
    status.textContent = msg || '';
    status.className = 'ap-valuation__status' + (kind ? ' is-' + kind : '');
  }
  function placeholderOption(sel) {
    return `<option value="">${esc(sel.dataset.ph || sel.querySelector('option')?.textContent || '')}</option>`;
  }
  function fillSelect(sel, values) {
    sel.innerHTML = placeholderOption(sel) + (values || []).map((v) => `<option value="${esc(v)}">${esc(v)}</option>`).join('');
    sel.disabled = !(values && values.length);
  }
  function fillYears(sel, from, to) {
    let out = placeholderOption(sel);
    for (let yv = to; yv >= from; yv--) out += `<option value="${yv}">${yv}</option>`;
    sel.innerHTML = out;
    sel.disabled = false;
  }
  function clearSelect(sel) { sel.innerHTML = placeholderOption(sel); sel.disabled = true; sel.value = ''; }

  function resetResult() { result.hidden = true; }
  function resetOptions() {
    optionSelects.forEach(clearSelect);
    mileageInputs.forEach((x) => { x.disabled = true; });
    submit.disabled = true;
    resetResult();
    setStatus('');
  }

  // ---- Dual-range mileage slider ----
  function updateFill() {
    const range = mileageCeil - mileageFloor || 1;
    const lo = (Number(mMinS.value) - mileageFloor) / range * 100;
    const hi = (Number(mMaxS.value) - mileageFloor) / range * 100;
    if (fill) { fill.style.left = lo + '%'; fill.style.width = Math.max(0, hi - lo) + '%'; }
  }
  function syncFromSlider() {
    let lo = Number(mMinS.value), hi = Number(mMaxS.value);
    if (lo > hi) { if (this === mMinS) { hi = lo; mMaxS.value = hi; } else { lo = hi; mMinS.value = lo; } }
    mMin.value = lo; mMax.value = hi; updateFill();
  }
  function syncFromNumber() {
    let lo = Math.max(mileageFloor, Math.min(Number(mMin.value) || mileageFloor, mileageCeil));
    let hi = Math.max(mileageFloor, Math.min(Number(mMax.value) || mileageCeil, mileageCeil));
    if (lo > hi) { if (this === mMin) hi = lo; else lo = hi; }
    mMinS.value = lo; mMaxS.value = hi; mMin.value = lo; mMax.value = hi; updateFill();
  }
  on(mMinS, 'input', syncFromSlider);
  on(mMaxS, 'input', syncFromSlider);
  on(mMin, 'change', syncFromNumber);
  on(mMax, 'change', syncFromNumber);

  function setMileageBounds(floor, ceil) {
    // Mileage is predefined to the full 0-300000 range and hidden from the UI
    // (client request), so ignore the per-model floor/ceil and always pin 0-300000.
    mileageFloor = 0;
    mileageCeil = 300000;
    [mMinS, mMaxS].forEach((s) => { s.min = mileageFloor; s.max = mileageCeil; });
    [mMin, mMax].forEach((n) => { n.min = mileageFloor; n.max = mileageCeil; });
    mMinS.value = mileageFloor; mMaxS.value = mileageCeil;
    mMin.value = mileageFloor; mMax.value = mileageCeil;
    updateFill();
  }

  // ---- Load the full 999 make catalog on init ----
  async function loadMakes() {
    const { ok, data } = await getJSON(`${API}valuation/makes`);
    const makes = (ok && Array.isArray(data)) ? data : [];
    if (!makes.length) {
      setStatus(t('val_unavailable', 'Serviciul de evaluare este momentan indisponibil.'), 'error');
      return;
    }
    brand.innerHTML = placeholderOption(brand) + makes.map((m) => `<option value="${esc(m.title)}" data-id="${Number(m.id)}">${esc(m.title)}</option>`).join('');
    brand.disabled = false;
  }
  loadMakes();

  // ---- Brand → models (999 dependent options for the selected make) ----
  on(brand, 'change', async () => {
    clearSelect(model); resetOptions();
    if (!brand.value) return;
    const makeId = brand.selectedOptions[0] ? brand.selectedOptions[0].dataset.id : '';
    if (!makeId) return;
    setStatus(t('val_loading', 'Se încarcă…'));
    const { ok, data } = await getJSON(`${API}valuation/models?make_id=${encodeURIComponent(makeId)}`);
    setStatus('');
    const models = (ok && Array.isArray(data)) ? data : [];
    if (!models.length) { setStatus(t('val_no_models', 'Nu avem modele pentru această marcă.'), 'error'); return; }
    model.innerHTML = placeholderOption(model) + models.map((m) => `<option value="${esc(m.title)}">${esc(m.title)}</option>`).join('');
    model.disabled = false;
  });

  // ---- Model → /valuation/options (from the API) ----
  on(model, 'change', async () => {
    resetOptions();
    if (!brand.value || !model.value) return;
    setStatus(t('val_loading', 'Se încarcă…'));
    const { ok, data } = await getJSON(`${API}valuation/options?brand=${encodeURIComponent(brand.value)}&model=${encodeURIComponent(model.value)}`);
    if (!ok || !data || !data.data) { setStatus(t('val_unavailable', 'Serviciul de evaluare este momentan indisponibil.'), 'error'); return; }
    setStatus('');
    const d = data.data;
    fillSelect(generation, d.generation);
    fillSelect(fuel, d.fuel);
    fillSelect(transmission, d.transmission);
    fillSelect(drive, d.drive_type);
    fillSelect(body, d.body_type);
    const ymin = (d.years && d.years.min) || 1990;
    const ymax = (d.years && d.years.max) || new Date().getFullYear();
    fillYears(startYear, ymin, ymax);
    fillYears(endYear, ymin, ymax);
    setMileageBounds(d.mileage && d.mileage.min, d.mileage && d.mileage.max);
    mileageInputs.forEach((x) => { x.disabled = false; });
    submit.disabled = false;
  });

  // ---- Submit → /valuation/price ----
  on(submit, 'click', async () => {
    if (!brand.value || !model.value) return;
    const params = new URLSearchParams({ brand: brand.value, model: model.value });
    const put = (k, v) => { if (v) params.set(k, v); };
    put('start_year', startYear.value); put('end_year', endYear.value);
    put('generation', generation.value); put('fuel', fuel.value);
    put('transmission', transmission.value); put('drive_type', drive.value); put('body_type', body.value);
    put('mileage_min', mMin.value); put('mileage_max', mMax.value);
    submit.disabled = true; setStatus(t('val_loading', 'Se încarcă…'));
    const { ok, data } = await getJSON(`${API}valuation/price?${params.toString()}`);
    submit.disabled = false;
    if (!ok || !data || !data.data) { setStatus(t('val_unavailable', 'Serviciul de evaluare este momentan indisponibil.'), 'error'); resetResult(); return; }
    const d = data.data;
    // Show the estimate whenever the source has a usable price - even a single
    // recent ad (client request). `enough_data` (nomermd needs >=4 ads in the
    // 30-day window) is ignored on purpose. If the current window happens to be
    // empty, fall back to the most recent history month that carries a price.
    let price = (Number(d.avg_price) > 0 && Number(d.ads_count) > 0) ? Number(d.avg_price) : 0;
    if (price <= 0 && Array.isArray(d.history)) {
      const recent = d.history
        .filter((h) => h && Number(h.avg_price) > 0)
        .sort((a, b) => String(b.month).localeCompare(String(a.month)));
      if (recent.length) price = Number(recent[0].avg_price);
    }
    if (price <= 0) {
      setStatus(t('val_no_data', 'Nu sunt suficiente anunțuri pentru această configurație. Încearcă filtre mai largi.'), 'error');
      resetResult();
      return;
    }
    setStatus('');
    priceEl.textContent = nfmt(price);
    renderChart(chart, d.history);
    result.hidden = false;
    result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });

  // ---- Reset: clear the whole form back to its initial state ----
  const reset = $('reset');
  if (reset) on(reset, 'click', () => {
    brand.value = '';
    // Dispatching 'change' on the emptied brand cascades the clear (model +
    // option selects via the handler above) and re-syncs both comboboxes.
    brand.dispatchEvent(new Event('change', { bubbles: true }));
    setMileageBounds(0, 300000);
    mileageInputs.forEach((x) => { x.disabled = true; });
    resetResult();
    setStatus('');
  });
}
